import _ from 'lodash'

import { DocumentFile } from './DocumentFile'
import { Member } from './Member'
import { AssociationGroup } from './AssociationGroup'
import { FrequentlyAskedQuestion } from './FrequentlyAskedQuestion'
import { Legislation } from './Legislation'
import { ImageItem } from './ImageItem'
import { Section } from './Section'
import { MODAL_MARKERS } from '../config/pks-constants'

class Association {
    constructor({
        id,
        name,
        sector,
        slug,
        description,
        publications,
        legislations,
        about_industry,
        e_services,
        documents,
        board,
        contacts,
        association_groups,
        faq,
        header_image,
        sections,
        projects
    }) {
        this.id = id
        this.name = name
        this.sector = sector
        this.associationSlug = slug
        this.description = description
        this.publicationList = publications
        this.legislationList = legislations
        this.aboutIndustry = about_industry
        this.eServices = e_services
        this.documentList = documents
        this.boardList = board
        this.contactList = contacts
        this.associationGroupList = association_groups
        this.frequentQuestionList = faq
        this.headerImage = header_image && new ImageItem(header_image)
        this.sectionsList = sections
        this.projects = projects
    }

    get slug() {
        return this.associationSlug || this.fallbackSlug
    }

    get fallbackSlug() {
        const slugName = this.name
            .toLowerCase()
            .replace(/\s+/g, '-') // Replace spaces with -
            .replace(/[^\w-]+/g, '') // Remove all non-word chars
            .replace(/^-+/, '') // Trim - from start of text
            .replace(/-+$/, '') // Trim - from end of text
        const slug = `${slugName}--${this.id}`
        return slug
    }

    get hasHtmlDescription() {
        return !_.isEmpty(this.description)
    }

    get truncatedDescription() {
        return _.truncate(this.description, {
            length: 270,
            separator: /\./,
            omission: '.'
        })
    }

    get htmlTruncatedDescription() {
        return {
            __html: this.truncatedDescription
        }
    }

    get htmlDescription() {
        return {
            __html: this.description
        }
    }

    get hasHtmlAboutIndustry() {
        return !_.isEmpty(this.aboutIndustry)
    }

    get htmlAboutIndustry() {
        return this.hasHtmlAboutIndustry
            ? {
                  __html: this.aboutIndustry
              }
            : null
    }

    get modalAboutIndustry() {
        const description = this.htmlAboutIndustry
        return this.hasHtmlAboutIndustry
            ? {
                  marker: MODAL_MARKERS.ABOUT_INDUSTRY,
                  icon: {
                      thumb: '/images/group-6.png',
                      bgImg: '/images/o-industriji.png'
                  },
                  description
              }
            : null
    }

    get hasPublications() {
        return !_.isEmpty(this.publicationList)
    }

    get publications() {
        return !_.isEmpty(this.publicationList)
            ? this.publicationList.map(document => new DocumentFile(document))
            : []
    }

    get modalPublications() {
        const description = this.publications
        return this.hasPublications
            ? {
                  marker: MODAL_MARKERS.PUBLICATIONS,
                  icon: {
                      thumb: '/images/group-5.png',
                      bgImg: '/images/publikacija.png'
                  },
                  description
              }
            : null
    }

    get hasLegislations() {
        return !_.isEmpty(this.legislationList)
    }

    get legislations() {
        return !_.isEmpty(this.legislationList)
            ? this.legislationList.map(item => new Legislation(item))
            : []
    }

    get modalLegislations() {
        const description = this.legislations
        return this.hasLegislations
            ? {
                  marker: MODAL_MARKERS.LEGISLATIONS,
                  icon: {
                      thumb: '/images/group-4.png',
                      bgImg: '/images/zakonske-regulative.png'
                  },
                  description
              }
            : null
    }

    get hasHtmlUsefulAddresses() {
        return !_.isEmpty(this.usefulAddresses)
    }

    get htmlUsefulAddresses() {
        return {
            __html: this.usefulAddresses
        }
    }

    get hasDocuments() {
        return !_.isEmpty(this.documentList)
    }

    get documents() {
        return this.documentList.map(document => new DocumentFile(document))
    }

    get hasBoard() {
        return !_.isEmpty(this.boardList)
    }

    get board() {
        return !_.isEmpty(this.boardList) ? this.boardList.map(item => new Member(item)) : []
    }

    get modalBoard() {
        const description = this.board
        return this.hasBoard
            ? {
                  marker: MODAL_MARKERS.BOARD,
                  icon: {
                      thumb: '/images/group-3.png',
                      bgImg: '/images/odbor.png'
                  },
                  description
              }
            : null
    }

    get hasContacts() {
        return !_.isEmpty(this.contactList)
    }

    get contacts() {
        return this.contactList.map(contactPerson => new Member(contactPerson))
    }

    get hasAssociationGroups() {
        return !_.isEmpty(this.associationGroupList)
    }

    get associationGroups() {
        return !_.isEmpty(this.associationGroupList)
            ? this.associationGroupList.map(
                  associationGroup => new AssociationGroup(associationGroup)
              )
            : []
    }

    get modalGroupations() {
        const description = this.associationGroups
        return this.hasAssociationGroups
            ? {
                  marker: MODAL_MARKERS.GROUPATIONS,
                  icon: {
                      thumb: '/images/group-7.png',
                      bgImg: '/images/grupacije.png'
                  },
                  description
              }
            : null
    }

    get hasFrequentQuestions() {
        return !_.isEmpty(this.frequentQuestionList)
    }

    get frequentQuestions() {
        return !_.isEmpty(this.frequentQuestionList)
            ? this.hasFrequentQuestions &&
                  this.frequentQuestionList.map(question => new FrequentlyAskedQuestion(question))
            : []
    }

    get originalImage() {
        return this.headerImage && this.headerImage.image.original
    }

    get associationImage() {
        return this.originalImage || '/images/association-default.jpg'
    }

    get hasSections() {
        return !_.isEmpty(this.sectionsList)
    }

    get sections() {
        return !_.isEmpty(this.sectionsList) ? this.sectionsList.map(item => new Section(item)) : []
    }

    get modalList() {
        return [
            this.modalBoard,
            this.modalGroupations,
            this.modalPublications,
            this.modalLegislations,
            this.modalAboutIndustry,
            this.modalProjects
        ]
    }

    get hasHtmlProjects() {
        return !_.isEmpty(this.projects)
    }

    get htmlProjects() {
        return this.hasHtmlProjects
            ? {
                  __html: this.projects
              }
            : null
    }

    get modalProjects() {
        const description = this.htmlProjects
        return this.hasHtmlProjects
            ? {
                  marker: MODAL_MARKERS.PROJECTS,
                  icon: {
                      thumb: '/images/group-6.png',
                      bgImg: '/images/o-industriji.png'
                  },
                  description
              }
            : null
    }

    get isOffice() {
        return (
            this.slug
                .split('-')
                .slice(0, 1)
                .join('') === 'sluzba'
        )
    }

    get associationType() {
        const type = this.name
            .split(' ')
            .slice(0, 1)
            .join('')
            .toLowerCase()
        let associationType = ''

        switch (true) {
            case type === 'služba' || type === 'служба':
                associationType = 'office'
                break
            case type === 'centar' || type === 'центар':
                associationType = 'center'
                break
            default:
                associationType = 'association'
                break
        }

        return associationType
    }
}

export { Association }
